import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { LockKeyhole, AlertCircle } from "lucide-react";
import BarkLogo from "../components/BarkLogo";
import { api, setToken, formatApiErrorDetail, getToken } from "../lib/api";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    document.title = "Admin Sign In | Bark Buddies";
    if (getToken()) navigate("/admin/beta-signups", { replace: true });
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      navigate("/admin/beta-signups", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bark-admin flex flex-col" data-testid="admin-login-page">
      <div className="p-5 md:p-8">
        <Link to="/" className="no-underline">
          <BarkLogo size={40} />
        </Link>
      </div>
      <div className="flex-1 grid place-items-center px-5">
        <form
          onSubmit={submit}
          className="w-full max-w-md bg-white rounded-[28px] shadow-bark-soft p-7 md:p-10"
          data-testid="admin-login-form"
        >
          <div className="w-12 h-12 rounded-2xl bg-bark-teal-soft grid place-items-center mb-4">
            <LockKeyhole size={22} className="text-bark-teal" />
          </div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight text-bark-ink">
            Admin sign in
          </h1>
          <p className="mt-2 text-[14px] text-bark-ink2">
            Sign in to manage Bark Buddies beta signups.
          </p>

          <div className="mt-6 space-y-4">
            <label className="block">
              <div className="text-[13px] font-bold text-bark-ink mb-1.5">Email</div>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-2xl bg-white border border-bark-teal/20 shadow-bark-soft px-4 py-3 text-[15px] text-bark-ink placeholder:text-bark-muted focus:border-bark-teal focus:outline-none"
                autoComplete="email"
                data-testid="admin-email"
              />
            </label>
            <label className="block">
              <div className="text-[13px] font-bold text-bark-ink mb-1.5">Password</div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-2xl bg-white border border-bark-teal/20 shadow-bark-soft px-4 py-3 text-[15px] text-bark-ink placeholder:text-bark-muted focus:border-bark-teal focus:outline-none"
                autoComplete="current-password"
                data-testid="admin-password"
              />
            </label>
          </div>

          {error && (
            <div
              className="mt-4 flex items-start gap-2 p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-[13px]"
              data-testid="admin-login-error"
            >
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="mt-6 w-full rounded-full bg-bark-teal px-6 py-3.5 text-white font-extrabold text-[15px] shadow-bark-soft hover:bg-bark-teal-hover hover:-translate-y-0.5 transition-transform duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
            data-testid="admin-login-submit"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
